<script setup lang="ts">
import dayjs from '@/utils/dayjs'
import { ua } from '@/utils/ua'
import Provider from './ui/provider/index.vue'
import 'dayjs/locale/en'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/zh-tw'

const route = useRoute()
const router = useRouter()

const settingsStore = useSettingsStore()
const userStore = useUserStore()

const { locale } = useI18n()
const { auth } = useAuth()
const { generateI18nTitle } = useMenu()

document.body.setAttribute('data-os', ua.getOS().name || '')
window.addEventListener('message', receiveMessage, false)
function receiveMessage(event: MessageEvent) {
  console.log('receiveMessage', event.data)
  const { position, baseUrl, token } = event.data
  if (position === 'yhy_cms_iframe_page') {
    localStorage.setItem('token', token)
    userStore.token = token
    localStorage.setItem('baseUrl', baseUrl)
  }
}
/**
 * 处理从 URL 参数中获取 token（用于 iframe 嵌入场景）
 * 旧后台通过 iframe 标签传递 token 参数
 * 例如：<iframe src="http://your-domain.com/#/your-page?token=xxx"></iframe>
 *
 * 注意：在 hash 模式下，查询参数在 hash 中，需要从 route.query 或 hash 中解析
 */
function handleTokenFromUrl() {
  // 方法1：优先使用 Vue Router 的 route.query（推荐，因为 Vue Router 会自动解析）
  let token = route.query.token as string | undefined

  // 方法2：如果 route.query 中没有，尝试从 hash 中解析（兼容性处理）
  if (!token) {
    const hash = window.location.hash
    if (hash.includes('?')) {
      const hashQueryString = hash.split('?')[1]
      const urlParams = new URLSearchParams(hashQueryString)
      token = urlParams.get('token') || undefined
    }
  }

  // 方法3：如果 hash 中也没有，尝试从 URL 的 search 参数中获取（非 hash 模式）
  if (!token) {
    const urlParams = new URLSearchParams(window.location.search)
    token = urlParams.get('token') || undefined
  }

  if (token) {
    // 将 token 写入 localStorage 和 userStore
    localStorage.setItem('token', token)
    userStore.token = token

    // 清除 URL 中的 token 参数，避免泄露
    const query = { ...route.query }
    delete query.token
    router.replace({
      path: route.path,
      query,
      hash: route.hash,
    }).catch(() => {})
  }
}

// 监听路由变化，处理 token（因为路由可能在组件创建后才解析完成）
// watch(() => route.query.token, (token) => {
//   if (token && !userStore.token) {
//     localStorage.setItem('token', token as string)
//     userStore.token = token as string
//     // 清除 URL 中的 token 参数
//     const query = { ...route.query }
//     delete query.token
//     router.replace({
//       path: route.path,
//       query,
//       hash: route.hash,
//     }).catch(() => {})
//   }
// }, { immediate: true })

// 在组件创建时也尝试处理一次（兼容性处理）
handleTokenFromUrl()

const isAuth = computed(() => {
  return route.matched.every((item) => {
    return auth(item.meta.auth ?? '')
  })
})

// 设置网页 title
watch([
  () => settingsStore.settings.app.enableDynamicTitle,
  () => settingsStore.title,
  () => settingsStore.customTitleList,
  () => settingsStore.lang,
], () => {
  nextTick(() => {
    if (settingsStore.settings.app.enableDynamicTitle && settingsStore.title) {
      const title = settingsStore.customTitleList.find(item => item.fullPath === route.fullPath)?.title || generateI18nTitle(settingsStore.title)
      document.title = `${title} - ${'影惠云(Beta) 专业版'}`
    }
    else {
      document.title = '影惠云(Beta) 专业版'
    }
  })
}, {
  immediate: true,
  deep: true,
})

onMounted(() => {
  settingsStore.setMode(document.documentElement.clientWidth)
  window.addEventListener('resize', () => {
    settingsStore.setMode(document.documentElement.clientWidth)
  })
})

watch(() => settingsStore.lang, () => {
  locale.value = settingsStore.lang
})

watch(() => settingsStore.lang, () => {
  switch (settingsStore.lang) {
    case 'zh-cn':
      dayjs.locale('zh-cn')
      break
    case 'zh-tw':
      dayjs.locale('zh-tw')
      break
    case 'en':
      dayjs.locale('en')
      break
  }
}, {
  immediate: true,
})
</script>

<template>
  <Provider>
    <RouterView v-slot="{ Component }">
      <component :is="Component" v-if="isAuth" />
      <FaNotAllowed v-else />
    </RouterView>
    <FaBackToTop />
    <FaLoginAgain />
    <FaCheckUpdates />
    <FaToast />
    <FaNotification />
    <FaSystemInfo />
  </Provider>
</template>
